import { TDomain } from '@/core/modules/domains/domains'
import { DomainsModule } from '@/core/modules/domains/domains.module'
import { Analytics } from '@core/services/mixpanel'
import { useState } from 'react'

export const NUM_OF_DOMAINS_ALLOWED = 10

export type TAddDomain = TDomain & { checked: boolean; deleted: boolean }

export type TDomainDiscoveryMap = {
    [key: string]: TAddDomain
}

export type TAddDomainTag = {
    id: string
    value: string
    is_valid: boolean
    status: 'selected' | 'disabled'
}

const useAddDomain = () => {
    const { discoveryScanDomains: discoveryDomains } = DomainsModule
    const { loading, error } = DomainsModule.useStore()
    const domainInputRef = useRef<HTMLInputElement>(null)
    const [addedDomains, setAddedDomains] = useState<TAddDomainTag[]>([])
    const [addDomainsError, setAddDomainsError] = useState<null | string>(null)
    const [domainCheckValue, setDomainCheckValue] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)

    const discoveryScanMapping = useMemo(
        () =>
            discoveryDomains.reduce((acc, curr) => {
                acc[curr.id] = {
                    ...curr,
                    checked: false,
                    deleted: false,
                }
                return acc
            }, {} as TDomainDiscoveryMap),
        [discoveryDomains]
    )

    const [discoveryScanDomains, setDiscoveryScanDomains] =
        useState<TDomainDiscoveryMap>(discoveryScanMapping)

    const domainDiscoveryDomainsArray = useMemo(
        () => Object.values(discoveryScanDomains),
        [discoveryScanDomains]
    )

    const isAllValid = useMemo(() => {
        return (
            addedDomains.length > 0 &&
            addedDomains.every((domain) => domain.is_valid)
        )
    }, [addedDomains])

    const hasDeletions = useMemo(
        () => domainDiscoveryDomainsArray.some((domain) => domain.deleted),
        [domainDiscoveryDomainsArray]
    )

    const isSaveEnabled = useMemo(
        () =>
            addedDomains.every((domain) => domain.is_valid) &&
            (addedDomains.length > 0 || hasDeletions),
        [addedDomains, hasDeletions]
    )

    useEffect(() => {
        setInputDisabled(false)
        setAddDomainsError('')
        if (isAllValid) {
            setAddDomainsError(null)
            setDomainCheckValue('')
        } else {
            if (addedDomains.length) {
                const tag = addedDomains.find((domain) => !domain.is_valid)
                tag && setDomainCheckValue(tag.value)
                setInputDisabled(true)
                setAddDomainsError(error)
            } else {
                setDomainCheckValue('')
            }
        }
    }, [addedDomains])

    const syncAddDomainsTags = (id: string, value: boolean) => {
        if (value) {
            addedDomains.length < NUM_OF_DOMAINS_ALLOWED &&
                addDomain(
                    {
                        id,
                        value: discoveryScanDomains[id].domain_url,
                        is_valid: true,
                        status: 'selected',
                    },
                    true
                )
        } else {
            removeTagFromDomains({
                id,
                value: discoveryScanDomains[id].domain_url,
                is_valid: false,
                status: 'selected',
            })
        }
    }

    const handleOnDomainChange = (id: string, value: boolean) => {
        syncAddDomainsTags(id, value)
        Analytics.event('SEM::user_selected_domain_from_discovery', {
            selected: value,
        })

        if (addedDomains.length < NUM_OF_DOMAINS_ALLOWED) {
            const updatedDomains = {
                ...discoveryScanDomains,
                [id]: { ...discoveryScanDomains[id], checked: value },
            }
            setDiscoveryScanDomains(updatedDomains)
        }
    }

    const handleOnDomainDiscoveryDeleted = (id: string) => {
        Analytics.event('SEM::user_removes_domain_from_discovery')
        setDiscoveryScanDomains((prev) => ({
            ...prev,
            [id]: { ...prev[id], deleted: true },
        }))
    }

    const handleOnDomainDiscoveryRestore = (id: string) => {
        Analytics.event('SEM::user_restores_domain_from_discovery')
        setDiscoveryScanDomains((prev) => ({
            ...prev,
            [id]: { ...prev[id], deleted: false },
        }))
    }

    const addDomains = async () => {
        const deletedDomains = domainDiscoveryDomainsArray
            .filter((domain) => domain.deleted)
            .map((domain): TAddDomainTag => {
                return {
                    id: domain.id,
                    value: domain.domain_url,
                    is_valid: true,
                    status: 'disabled',
                }
            })

        const result = await DomainsModule.submitDomains([
            ...addedDomains,
            ...deletedDomains,
        ])
        if (result?.valid) {
            return true
        }
        return false
    }

    const resetInput = () => {
        if (domainInputRef.current) {
            domainInputRef.current.value = ''
        }
    }

    const onDomainAdd = async (tag: TAddDomainTag) => {
        setAddDomainsError(null)

        // if domain is already in the list, skip it and reset input
        if (addedDomains.map(({ id }) => id).includes(tag.id)) {
            resetInput()
            return
        }

        // verify domain and add to the list
        const { isValid, errorMessage } =
            await DomainsModule.checkDomainValidity(tag.value)
        if (!isValid) {
            errorMessage && setAddDomainsError(errorMessage)
            setDomainCheckValue(tag.value)
        } else {
            addDomain(tag, isValid)
        }
    }

    const onDomainEdit = async (tag: TAddDomainTag) => {
        setAddDomainsError(null)

        const domainToUpdate = {
            ...addedDomains.find((domain) => domain.id === tag.id),
        } as TAddDomainTag

        const isValueAlreadyExist = addedDomains.some(
            ({ value }) => value === tag.value
        )

        if (isValueAlreadyExist) {
            const updatedDomains = addedDomains.filter(
                ({ id }) => id !== tag.id
            )
            setAddedDomains(updatedDomains)
            return
        }

        if (domainToUpdate) {
            const { isValid } = await DomainsModule.checkDomainValidity(
                tag.value
            )

            domainToUpdate.value = tag.value
            domainToUpdate.is_valid = isValid
            const updatedDomains = addedDomains.map((domain) =>
                domain.id === domainToUpdate.id ? domainToUpdate : domain
            )
            setAddedDomains(updatedDomains)
        }
    }

    const removeTagFromDomains = (tag: TAddDomainTag) => {
        if (discoveryScanDomains[tag.id]) {
            const updatedDiscoveryDomains = {
                ...discoveryScanDomains,
                [tag.id]: { ...discoveryScanDomains[tag.id], checked: false },
            }
            setDiscoveryScanDomains(updatedDiscoveryDomains)
        }

        const updatedDomains = addedDomains.filter(({ id }) => id !== tag.id)
        setAddedDomains(updatedDomains)
    }

    const addDomain = (tag: TAddDomainTag, isValid: boolean) => {
        resetInput()
        setAddedDomains([
            ...addedDomains,
            { ...tag, is_valid: isValid, status: 'selected' },
        ])
    }

    return {
        domains: addedDomains,
        onDomainAdd,
        inputDisabled,
        onDomainEdit,
        removeTagFromDomains,
        domainCheckValue,
        addDomainsError,
        addDomains,
        isSaveEnabled,
        loading,
        domainInputRef,
        handleOnDomainChange,
        handleOnDomainDiscoveryDeleted,
        handleOnDomainDiscoveryRestore,
        domainDiscoveryDomainsArray,
    }
}

export default useAddDomain
